import { Link, NavLink, useLocation } from "react-router-dom";
import { Briefcase, Heart, ClipboardList, User, LogOut, LayoutDashboard, Shield } from "lucide-react";
import { useT } from "../lib/i18n";
import { useAuth, startLogin, homeFor } from "../lib/auth";
import { IMG } from "../lib/options";

export const Logo = ({ size = 48, testid = "brand-logo" }) => (
  <img src="/img/logo.webp" width={size} height={size} alt="Work in Shop" data-testid={testid} style={{ width: size, height: size }} className="shrink-0" />
);

export const Photo = ({ k, className = "", eager = false, sizes = "(max-width: 768px) 100vw, 50vw" }) => {
  const { lang } = useT();
  const im = IMG[k];
  return (
    <img src={im.src} srcSet={im.srcSet} sizes={sizes} alt={im[lang]} loading={eager ? "eager" : "lazy"} decoding="async"
      fetchpriority={eager ? "high" : "auto"} className={`object-cover ${className}`} data-testid={`photo-${k}`} />
  );
};

export const LangToggle = () => {
  const { lang, setLang } = useT();
  return (
    <div className="flex rounded-full border-2 border-slate-200 p-0.5 text-sm font-bold" role="group" aria-label="Language">
      {["fr", "en"].map((l) => (
        <button key={l} onClick={() => setLang(l)} data-testid={`lang-toggle-${l}`} aria-pressed={lang === l}
          className={`min-h-[36px] min-w-[44px] rounded-full px-3 uppercase transition-colors ${lang === l ? "bg-[color:var(--navy)] text-white" : "text-slate-600"}`}>
          {l}
        </button>
      ))}
    </div>
  );
};

export function Header() {
  const { t } = useT();
  const { user } = useAuth();
  const loc = useLocation();
  const link = "hidden md:inline-flex items-center gap-2 px-3 py-2 rounded-xl font-semibold text-slate-700 hover:bg-slate-100 transition-colors";
  return (
    <header className="sticky top-0 z-40 border-b border-slate-200 bg-white/90 backdrop-blur-md">
      <div className="page flex h-16 items-center justify-between gap-2">
        <Link to="/" className="flex items-center gap-2" data-testid="nav-home-link">
          <Logo size={44} />
          <span className="font-display text-lg font-extrabold leading-none text-[color:var(--navy)] hidden min-[380px]:block">Work in <span className="text-[color:var(--orange)]">Shop</span></span>
        </Link>
        <nav className="flex items-center gap-1 sm:gap-2">
          <NavLink to="/jobs" className={link} data-testid="nav-jobs-link"><Briefcase size={18} />{t("jobs")}</NavLink>
          {(!user || user.role !== "candidate") && (
            <NavLink to={user?.role === "employer" ? "/employer" : "/employers"} className={link} data-testid="nav-employer-link"><LayoutDashboard size={18} />{t("for_shops")}</NavLink>
          )}
          {user?.role === "candidate" && (
            <>
              <NavLink to="/me/applications" className={link} data-testid="nav-applications-link"><ClipboardList size={18} />{t("applications")}</NavLink>
              <NavLink to="/me/profile" className={link} data-testid="nav-profile-link"><User size={18} />{t("profile")}</NavLink>
            </>
          )}
          {user?.role === "admin" && <NavLink to="/admin" className={link} data-testid="nav-admin-link"><Shield size={18} />{t("admin")}</NavLink>}
          <LangToggle />
          {!user ? (
            <button onClick={() => startLogin(loc.pathname)} data-testid="nav-signin-btn"
              className="min-h-[44px] rounded-full bg-[color:var(--navy)] px-4 text-sm font-bold text-white hover:bg-[color:var(--navy-dark)] transition-colors">
              {t("sign_in")}
            </button>
          ) : (
            <Link to={homeFor(user)} data-testid="nav-account-link" className="flex items-center">
              {user.picture ? <img src={user.picture} alt="" className="h-10 w-10 rounded-full border-2 border-slate-200" /> : <span className="grid h-10 w-10 place-items-center rounded-full bg-slate-100"><User size={20} /></span>}
            </Link>
          )}
        </nav>
      </div>
    </header>
  );
}

export function BottomNav() {
  const { t } = useT();
  const items = [
    { to: "/jobs", icon: Briefcase, label: t("jobs"), id: "jobs" },
    { to: "/me/saved", icon: Heart, label: t("saved"), id: "saved" },
    { to: "/me/applications", icon: ClipboardList, label: t("applications"), id: "applications" },
    { to: "/me/profile", icon: User, label: t("profile"), id: "profile" },
  ];
  return (
    <nav className="fixed inset-x-0 bottom-0 z-40 border-t border-slate-200 bg-white pb-safe md:hidden" data-testid="candidate-bottom-nav">
      <div className="grid grid-cols-4">
        {items.map(({ to, icon: Icon, label, id }) => (
          <NavLink key={to} to={to} data-testid={`bottom-nav-${id}`}
            className={({ isActive }) => `flex min-h-[64px] flex-col items-center justify-center gap-1 text-xs font-bold transition-colors ${isActive ? "text-[color:var(--orange)]" : "text-slate-500"}`}>
            <Icon size={24} strokeWidth={2.2} />
            {label}
          </NavLink>
        ))}
      </div>
    </nav>
  );
}

export function Footer() {
  const { t } = useT();
  return (
    <footer className="mt-16 bg-[color:var(--navy)] text-white" data-testid="site-footer">
      <div className="page flex flex-col gap-6 py-10 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-4">
          <span className="rounded-full bg-white p-1"><Logo size={64} testid="footer-logo" /></span>
          <div>
            <p className="font-display text-xl font-bold">Work in Shop</p>
            <p className="text-white/80">{t("footer_tag")}</p>
          </div>
        </div>
        <div className="text-white/90">
          <p className="font-semibold">{t("contact")}</p>
          <a href="mailto:info@workinshop.com" className="underline-offset-4 hover:underline" data-testid="footer-email">info@workinshop.com</a>
          <p className="text-sm text-white/60">workinshop.com · © {new Date().getFullYear()}</p>
        </div>
      </div>
    </footer>
  );
}
